/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
// import { useStateContext } from '../../../contexts/ContextProvider';
import { Worker } from '@react-pdf-viewer/core';
import { Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import Grid from '@mui/material/Grid';

function containsB64(item) {
  return Object.prototype.hasOwnProperty.call(item, 'b64');
}

function containsError(item) {
  return Object.prototype.hasOwnProperty.call(item, 'Error');
}

function base64ToUint8Array(base64) {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

const PdfViewer = ({ selectedFileContent, fileId }) => {
  const [pdfData, setPdfData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const processedContentRef = useRef(null);
  const isMountedRef = useRef(true);
  const blobUrlRef = useRef(null);

  // Create renderToolbar function - memoized but called unconditionally
  const renderToolbar = useMemo(() => (Toolbar) => (
    <Toolbar>
      {(slots) => {
        const {
          CurrentPageInput,
          GoToNextPage,
          GoToPreviousPage,
          NumberOfPages,
          Zoom,
          ZoomIn,
          ZoomOut,
          Rotate
        } = slots;
        return (
          <Grid container spacing={2} style={{ textAlign: 'center' }}>
            {/* PAGES SELECTION */}
            <Grid item xs={4} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center' }} >
              <GoToPreviousPage />
              <CurrentPageInput /> /
              <NumberOfPages />
              <GoToNextPage />
            </Grid>
            {/* ZOOM */}
            <Grid item xs={4} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center' }} >
              <ZoomOut />
              <Zoom />
              <ZoomIn />
            </Grid>
            {/* ROTATE */}
            <Grid item xs={4} style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center' }} >
              <Rotate />
            </Grid>
          </Grid>
        );
      }}
    </Toolbar>
  ), []);

  // Create plugin instance unconditionally at top level - defaultLayoutPlugin uses hooks internally
  // Since defaultLayoutPlugin uses hooks, it MUST be called unconditionally on every render
  // We cannot conditionally call it or wrap it in useMemo - React requires hooks to be called in the same order
  // The plugin instance will be recreated on each render, but React PDF Viewer handles this efficiently
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: () => [],
  });

  // Use fileId as key if available, otherwise fallback to a hash of the first part of b64
  // This ensures the viewer doesn't remount unnecessarily
  const viewerKey = useMemo(() => {
    return fileId || (selectedFileContent && containsB64(selectedFileContent) ? `pdf-${selectedFileContent.b64?.substring(0, 20)}` : 'pdf-viewer');
  }, [fileId, selectedFileContent]);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      // Clean up blob URL when component unmounts
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Don't process if component is not mounted or content is null/undefined
    if (!selectedFileContent || !isMountedRef.current) {
      return;
    }

    // Only process if the content actually changed
    // Use a hash or simple comparison to avoid reprocessing the same content
    const currentB64 = containsB64(selectedFileContent) ? selectedFileContent.b64 : null;
    
    // Skip if we already processed this exact content
    if (currentB64 === processedContentRef.current) {
      return;
    }

    // Clean up previous blob URL if it exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (containsB64(selectedFileContent)) {
      try {
        if (isMountedRef.current) {
          setIsLoading(true);
        }
        // Create a fresh Uint8Array from base64
        const convertedUint8 = base64ToUint8Array(selectedFileContent.b64);
        // Create a Blob from the Uint8Array to avoid ArrayBuffer detachment issues
        const blob = new Blob([convertedUint8], { type: 'application/pdf' });
        // Create a blob URL that's stable and won't get detached
        const blobUrl = URL.createObjectURL(blob);
        
        if (isMountedRef.current) {
          setPdfData(blobUrl);
          blobUrlRef.current = blobUrl;
          processedContentRef.current = currentB64;
        } else {
          // Component unmounted, clean up the blob URL
          URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.error('Failed to convert base64 to PDF:', error);
        if (isMountedRef.current) {
          setPdfData(null);
          processedContentRef.current = null;
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    } else {
      if (isMountedRef.current) {
        setPdfData(null);
        setIsLoading(false);
        processedContentRef.current = null;
      }
    }
  }, [selectedFileContent]);

  // Render conditionally - all hooks must be called before any conditional returns
  // Handle null/undefined or error cases
  if (!selectedFileContent) {
    return <p>Nessun file selezionato</p>;
  }

  if (containsError(selectedFileContent)) {
    return <p>Errore visualizzazione pdf: {selectedFileContent.Error}</p>;
  }

  if (isLoading) {
    return <p>Caricamento PDF...</p>;
  }

  if (!pdfData) {
    return <p>PDF non valido</p>;
  }

  return (
    <div className='scrollbar-data-grid' style={{ height: '650px' }}>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
        <Viewer 
          fileUrl={pdfData} 
          plugins={[defaultLayoutPluginInstance]}
          key={viewerKey}
        />
      </Worker>
    </div>
  );
};

export default PdfViewer;